import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MailIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Modal,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  useDebouncedValue,
  type DataTableSort,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  EMAIL_LOG_STATUS,
  PAGE_SIZE,
  listEmailLog,
  readEmailLogEntry,
  type EmailLogRow,
  type EmailLogStatus,
} from '@/lib/email-log';
import { useUrlPage } from '@/lib/use-list-query-state';
import { useTableSort } from '@/lib/use-table-sort';

/**
 * Every email the API tried to send, newest first: who it went to, what it
 * said, which part of the system sent it, and what became of it. A row
 * opens to the message as sent.
 */

const SORT_FIELDS = { when: 'sentAt', subject: 'subject', status: 'status', source: 'source' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.when, direction: 'desc' };

const STATUS_LABELS: Readonly<Record<EmailLogStatus, string>> = {
  [EMAIL_LOG_STATUS.sent]: 'Sent',
  [EMAIL_LOG_STATUS.redirected]: 'Redirected',
  [EMAIL_LOG_STATUS.suppressed]: 'Suppressed',
  [EMAIL_LOG_STATUS.failed]: 'Failed',
};
const STATUS_TONES: Readonly<Record<EmailLogStatus, PillTone>> = {
  [EMAIL_LOG_STATUS.sent]: 'success',
  [EMAIL_LOG_STATUS.redirected]: 'info',
  [EMAIL_LOG_STATUS.suppressed]: 'neutral',
  [EMAIL_LOG_STATUS.failed]: 'danger',
};

/** The status filter's choices: everything, or one outcome. */
const ALL = 'all';
const STATUS_OPTIONS = [
  { key: ALL, label: 'All' },
  ...Object.values(EMAIL_LOG_STATUS).map((status) => ({ key: status, label: STATUS_LABELS[status] })),
];

function isStatus(value: string): value is EmailLogStatus {
  return (Object.values(EMAIL_LOG_STATUS) as readonly string[]).includes(value);
}

function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function EmailLogTab(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const debounced = useDebouncedValue(search, 300);
  const statusParam = params.get('status') ?? ALL;
  const status = isStatus(statusParam) ? statusParam : undefined;
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const [openId, setOpenId] = useState<string | null>(null);

  const log = useQuery({
    queryKey: ['email-log', debounced, status, sort, page],
    queryFn: ({ signal }) =>
      listEmailLog(
        { page, search: debounced, status, sort: sort?.key ?? SORT_FIELDS.when, dir: sort?.direction ?? 'desc' },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search the email log"
          placeholder="Address or subject"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl
          label="Which emails to show"
          options={STATUS_OPTIONS}
          value={statusParam}
          onChange={(next) =>
            setParams((previous) => {
              const nextParams = new URLSearchParams(previous);
              if (next === ALL) nextParams.delete('status');
              else nextParams.set('status', next);
              nextParams.delete('page');
              return nextParams;
            })
          }
        />
      </div>

      {log.isPending ? (
        <SkeletonTable rows={8} columns={['When', 'To', 'Subject', 'Sent by', 'Outcome']} />
      ) : log.isError ? (
        <ErrorNotice title="Email log unavailable" message={errorMessage(log.error)} />
      ) : log.data.items.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<MailIcon aria-hidden className="size-6" />}
            title={debounced || status ? 'No emails match' : 'No emails yet'}
            message={debounced || status ? 'Try another address, subject or outcome.' : 'Every email the system sends shows here, with what it said and what became of it.'}
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'When', sortKey: SORT_FIELDS.when },
              'To',
              { label: 'Subject', sortKey: SORT_FIELDS.subject },
              { label: 'Sent by', sortKey: SORT_FIELDS.source },
              { label: 'Outcome', sortKey: SORT_FIELDS.status },
            ]}
            sort={sort}
            onSort={onSort}
            minWidth={900}
          >
            {log.data.items.map((row) => (
              <EmailRow key={row.id} row={row} onOpen={() => setOpenId(row.id)} />
            ))}
          </DataTable>
          {log.data.pagination.totalPages > 1 ? (
            <Pagination
              page={page}
              totalPages={log.data.pagination.totalPages}
              onChange={setPage}
              summary={`${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + log.data.items.length} of ${log.data.pagination.total}`}
            />
          ) : null}
        </>
      )}

      <EmailDetail id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function EmailRow({ row, onOpen }: { readonly row: EmailLogRow; readonly onOpen: () => void }): React.ReactElement {
  return (
    <tr className="border-t border-portal-line align-top">
      <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{whenLabel(row.sentAt)}</td>
      <td className="p-3 text-portal-small">
        <span className="block">{row.to.join(', ')}</span>
        {row.redirectedTo ? (
          <span className="block text-portal-micro text-portal-muted">went to {row.redirectedTo}</span>
        ) : null}
      </td>
      <td className="p-3 text-portal-small">
        <button type="button" onClick={onOpen} className="text-left font-bold text-brand hover:underline">
          {row.subject}
        </button>
        {row.error ? <span className="block text-portal-micro text-rose-700">{row.error}</span> : null}
      </td>
      <td className="p-3 text-portal-small text-portal-muted">{row.source}</td>
      <td className="p-3 text-portal-small whitespace-nowrap">
        <Pill tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Pill>
      </td>
    </tr>
  );
}

/** One email as it was sent, in a dialog: the facts, then the message in a sandboxed frame. */
function EmailDetail({ id, onClose }: { readonly id: string | null; readonly onClose: () => void }): React.ReactElement {
  const detail = useQuery({
    queryKey: ['email-log-entry', id],
    queryFn: ({ signal }) => readEmailLogEntry(id ?? '', signal),
    enabled: id !== null,
  });
  const entry = detail.data;

  return (
    <Modal
      open={id !== null}
      title={entry?.subject ?? 'Email'}
      subtitle={entry ? `${STATUS_LABELS[entry.status]} · ${whenLabel(entry.sentAt)} · sent by ${entry.source}` : 'Loading…'}
      onClose={onClose}
    >
      {detail.isPending && id !== null ? (
        <LoadingState label="Loading the email…" />
      ) : detail.isError ? (
        <ErrorNotice inline title="Email unavailable" message={errorMessage(detail.error)} />
      ) : entry ? (
        <div className="space-y-4">
          <dl className="grid gap-x-6 gap-y-2 text-portal-small sm:grid-cols-2">
            <div>
              <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">To</dt>
              <dd className="break-words">{entry.to.join(', ')}</dd>
            </div>
            <div>
              <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">From</dt>
              <dd className="break-words">{entry.from}</dd>
            </div>
            {entry.redirectedTo ? (
              <div>
                <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">Went to</dt>
                <dd className="break-words">{entry.redirectedTo}</dd>
              </div>
            ) : null}
            {entry.replyTo ? (
              <div>
                <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">Reply to</dt>
                <dd className="break-words">{entry.replyTo}</dd>
              </div>
            ) : null}
            {entry.error ? (
              <div className="sm:col-span-2">
                <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">Error</dt>
                <dd className="break-words text-rose-700">{entry.error}</dd>
              </div>
            ) : null}
            {entry.attachmentNames.length > 0 ? (
              <div className="sm:col-span-2">
                <dt className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">Attachments</dt>
                <dd className="break-words">{entry.attachmentNames.join(', ')}</dd>
              </div>
            ) : null}
          </dl>
          {entry.html ? (
            <iframe
              title="The email as sent"
              sandbox=""
              srcDoc={entry.html}
              className="h-[28rem] w-full rounded-portal-button border border-portal-line bg-white"
            />
          ) : (
            <pre className="whitespace-pre-wrap rounded-portal-button border border-portal-line bg-portal-bg p-3 text-portal-small">
              {entry.text ?? 'No body was recorded.'}
            </pre>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
